import cx from "classnames";
import { type FC, useMemo } from "react";

import { ChevronDownIcon } from "@moment/icons";

import { AvatarSize } from "../../Avatar";
import { Button } from "../../Button";
import { DropdownMenu } from "../../DropdownMenu";
import { UserAvatarAlpha } from "../../lib-canvas/UserAvatarAlpha";
import { type DataGridUser } from "../Props";
import { type TableCellValueCommonProps } from "./types";

export interface TableCellValueUserProps extends TableCellValueCommonProps {
	currentUser?: DataGridUser | null;
	users: DataGridUser[];
	renderAsRowDetail?: boolean;
}

export const TableCellValuePerson: FC<TableCellValueUserProps> = ({
	value,
	currentUser,
	users,
	onChange,
	renderAsRowDetail,
	isEditable,
}) => {
	const otherUsers = useMemo(
		() => users.filter((user) => user.id !== currentUser?.id),
		[users, currentUser]
	);

	const content = (
		<DropdownMenu.Content
			className="text-secondary [&_.DropdownMenuContentScrollContainer]:max-h-[20rem]"
			align="start"
		>
			<DropdownMenu.Item className="text-secondary" onSelect={() => onChange?.(undefined)}>
				Blank
			</DropdownMenu.Item>
			{currentUser && (
				<DropdownMenu.Item onSelect={() => onChange?.(currentUser)}>
					<UserAvatarAlpha
						size={AvatarSize.sm}
						imageURL={currentUser.profilePicture}
						label={currentUser.name}
					/>
				</DropdownMenu.Item>
			)}
			{users.length === 0 ? (
				<>
					<DropdownMenu.Separator />
					<div className="px-4 py-2 italic text-tertiary">Log in to assign yourself!</div>
				</>
			) : undefined}
			{otherUsers.map((user) => (
				<DropdownMenu.Item key={user.id} onSelect={() => onChange?.(user)}>
					<UserAvatarAlpha
						size={AvatarSize.sm}
						imageURL={user.profilePicture}
						label={user.name}
					/>
				</DropdownMenu.Item>
			))}
		</DropdownMenu.Content>
	);

	const buttonContent = value ? (
		<>
			<div className="flex w-full min-w-0 items-center gap-2">
				<UserAvatarAlpha
					size={AvatarSize.sm}
					imageURL={value.profilePicture}
					label={value.name}
				/>
			</div>
			<ChevronDownIcon className="ml-auto shrink-0" />
		</>
	) : (
		<ChevronDownIcon className="ml-auto shrink-0" />
	);

	const editableContent = (
		<div
			className={cx("flex flex-col", {
				"w-full": !renderAsRowDetail,
				"w-48": renderAsRowDetail,
			})}
		>
			<DropdownMenu.Root>
				<DropdownMenu.Trigger asChild disabled={!isEditable}>
					<Button
						level={renderAsRowDetail ? "secondary" : "quaternary"}
						className={cx("h-9 !no-underline", {
							"-m-2": !renderAsRowDetail,
							"!bg-transparent": !isEditable,
						})}
					>
						{buttonContent}
					</Button>
				</DropdownMenu.Trigger>

				{renderAsRowDetail ? <DropdownMenu.Portal>{content}</DropdownMenu.Portal> : content}
			</DropdownMenu.Root>
		</div>
	);

	return isEditable ? editableContent : buttonContent;
};
